import classNames from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useMyMonitoringDeleteControl, useMyMonitoringGetControlWithState } from '@apps-common/apis/my-monitoring/control'
import { useDeleteUnitControl, useGetUnitControlWithState } from '@apps-common/apis/portal/control'
import { ControlOptionsModal } from '@apps-common/apps/portal/pages/units/pages/manage/components'
import { ModalButton } from '@apps-common/components/modals/ModalButton'
import { useHistoryModal } from '@apps-common/components/modals/unit-history'
import { CONTROL_STATE_OFF, CONTROL_STATE_ON, CONTROL_STATE_UNKNOWN, ControlContextListener } from '@apps-common/config/controls'
import { ControlRefreshOptions, useControlsContext } from '@apps-common/contexts/ControlContext'
import { ResolvedControlTheme, useControlTheme } from '@apps-common/hooks/useControlTheme'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import { invalidateAllControlQueries } from '@apps-common/lib/invalidateControlQueries'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { IUnitControlWithStateModel } from '@apps-common/types/portal/unit-control'
import { useIonModal } from '@ionic/react'
import { ControlModifyModal } from '../../../pages/controls/pages/modify/modals'
import { getControlType } from '../control-type'

const normalizeControlState = (value: unknown) => {
  if (value === null || value === undefined) {
    return CONTROL_STATE_UNKNOWN
  }

  const normalized = String(value).toLowerCase()

  if (normalized === CONTROL_STATE_ON) {
    return CONTROL_STATE_ON
  }

  if (normalized === CONTROL_STATE_OFF) {
    return CONTROL_STATE_OFF
  }

  return CONTROL_STATE_UNKNOWN
}

export const buildStateControlIcon = (control: IUnitControlWithStateModel, colors: ResolvedControlTheme) => {
  const { lastKnownState, currentState } = control
  const state = normalizeControlState(currentState ?? lastKnownState)

  if (state?.toLowerCase() === CONTROL_STATE_OFF) {
    return <div className={classNames('uppercase jost-bold text-[0.85em]', colors.text)}>off</div>
  }
  if (state?.toLowerCase() === CONTROL_STATE_ON) {
    return <div className={classNames('uppercase jost-bold text-[0.85rem]', colors.text)}>on</div>
  }

  return <div className={classNames('uppercase jost-bold text-[1.1rem]', colors.text)}>?</div>
}

interface IProps {
  // navigation?: IAppNavigation
  unitStatus: string
  isActivated: boolean
  containerClasses?: string
  control: IUnitControlWithStateModel
  onStateUpdatedCallback?: (data: IUnitControlWithStateModel) => void
  idPrefix: string
  showDefaultControlOptions?: boolean
}

export const StateControlButton = ({
  // navigation,
  unitStatus,
  isActivated,
  containerClasses,
  control,
  onStateUpdatedCallback,
  idPrefix,
  showDefaultControlOptions
}: IProps) => {
  const [controlRef, setControlRef] = useState<IUnitControlWithStateModel>(control)

  const controlType = getControlType(controlRef.controlTypeId, controlRef.requiresControlState)
  const { resolve } = useControlTheme()
  const normalizedState = normalizeControlState(controlRef.currentState)
  const colors = resolve(controlType, normalizedState)

  const { activate, getAwaiting, subscribe, unsubscribe } = useControlsContext()
  const { unitId, unitControlId, controlName, requiresControlState } = controlRef
  const refreshOptionsRef = useRef<ControlRefreshOptions | undefined>(undefined)

  const { open } = useHistoryModal({ type: 'control', name: controlName, id: unitControlId, requiresState: requiresControlState })

  const useGetControlWithState = useProjectHook(useGetUnitControlWithState, useMyMonitoringGetControlWithState)
  const useDeleteControl = useProjectHook(useDeleteUnitControl, useMyMonitoringDeleteControl)

  const { mutate: refreshControl } = useGetControlWithState(false, (data: IUnitControlWithStateModel) => {
    refreshOptionsRef.current?.onRefreshed?.(data)
    refreshOptionsRef.current = undefined
    setControlRef(data)
  })

  const { mutate: deleteMutation, isPending: isDeleting } = useDeleteControl(true, () => {
    invalidateAllControlQueries(unitId, control?.unitControlId)
  })

  const [present, dismiss] = useIonModal(ControlModifyModal, {
    componentProps: {
      unitId,
      controlId: unitControlId
    },
    onDismiss: (data: string, role: string) => dismiss(data, role),
    enableBackdropDismiss: true
  })

  useEffect(() => {
    setControlRef(control)
  }, [control])

  const onControlStateUpdated = useCallback(
    (data: IUnitControlWithStateModel) => {
      setControlRef(data)
      if (onStateUpdatedCallback) {
        onStateUpdatedCallback(data)
      }
    },
    [onStateUpdatedCallback]
  )

  const onRefresh = useCallback(
    (options?: ControlRefreshOptions) => {
      refreshOptionsRef.current = options
      refreshControl({ controlId: controlRef.unitControlId })
    },
    [controlRef.unitControlId, refreshControl]
  )

  useEffect(() => {
    const listenerId = `${idPrefix}-${unitControlId}`
    subscribe({
      sourceId: ControlContextListener.UnitManagement,
      listenerId,
      control: controlRef,
      callback: onControlStateUpdated,
      refresh: onRefresh
    })

    return () =>
      unsubscribe({
        sourceId: ControlContextListener.UnitManagement,
        listenerId,
        control: controlRef,
        callback: onControlStateUpdated,
        refresh: onRefresh
      })
  }, [controlRef, idPrefix, onControlStateUpdated, onRefresh, subscribe, unitControlId, unsubscribe])

  const nameSplit = (controlName || '').split(' ')

  const onOptionSelected = ({ action, command }: { action: string; command: string }) => {
    if (action === 'edit') {
      present({
        enterAnimation: modalSlideUpEnterAnimation,
        leaveAnimation: modalSlideUpLeaveAnimation
      })
    } else if (action === 'delete') {
      deleteMutation({ controlId: unitControlId })
    } else if (action === 'activate') {
      activate(controlRef, command)
    } else if (action === 'history') {
      open()
    }
  }

  const isSending = getAwaiting(controlRef.unitControlId)?.status === 'sending'
  const isAwaiting = getAwaiting(controlRef.unitControlId)?.status === 'awaiting-update'

  const isOn = normalizedState === CONTROL_STATE_ON

  return (
    <ModalButton
      classes='w-full'
      component={ControlOptionsModal}
      disabled={isDeleting}
      componentProps={{ unitStatus, isActivated, control: controlRef, showDefaultControlOptions }}
      confirmCallbackAfterDismiss={onOptionSelected}
      enterAnimation={modalSlideUpEnterAnimation}
      leaveAnimation={modalSlideUpLeaveAnimation}
    >
      <div
        className={classNames(
          containerClasses,
          'relative w-full pt-[100%] hover:cursor-pointer hover:opacity-80 rounded-[35px] flex items-center justify-start gap-[0px] border-[1px] border-secondary-content/10',
          isDeleting && '!opacity-50',
          colors.bg
        )}
      >
        <div className='absolute top-0 left-0 flex flex-col items-between justify-center w-full h-[100%]'>
          <div className={classNames('grow flex items-center justify-center w-full rounded-[40px] text-center')}>
            <div className=' w-[55px] h-[55px] flex items-center justify-center p-[3px]'>
              <div className='absolute'>
                <div
                  className={classNames((isSending || isAwaiting) && 'control-spinner', isOn && 'control-spinner-reverse', colors.border)}
                />
              </div>
              <div className={classNames('w-full h-[100%] rounded-[50%] flex items-center justify-center', colors.textBg)}>
                {buildStateControlIcon(controlRef, colors)}
              </div>
            </div>
          </div>

          <p className={classNames('grow uppercase text-[0.7rem] jost leading-[0.8rem] text-center', colors.text)}>
            {nameSplit.slice(0, 2).map((w, i) => (
              <span key={`state-control-name-part-${i}`} className='block text-ellipsis whitespace-nowrap overflow-hidden'>
                {w}
              </span>
            ))}
            {nameSplit.length > 2 && <span className='absolute bottom-[-0.5em]'>...</span>}
          </p>
        </div>
      </div>
    </ModalButton>
  )
}
